import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { InputText } from 'primeng/inputtext';
import { Panel } from 'primeng/panel';
import { Select } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { WebAnalyticsUiService } from '../../services/web-analytics-ui.service';
import {
  dashboardFiltersFromQuery,
  queryParamsFromDashboardFilters,
} from '../../utils/web-analytics-route-sync';
import {
  WebAnalyticsDashboardFilters,
  WebAnalyticsService,
} from '../../services/web-analytics.service';

interface AppIdOption {
  /** Empty string means "all apps". */
  value: string;
  label: string;
}

/** Real host-app ids only — hide internal DynamoDB key fragments. */
function isCustomerAppId(id: string): boolean {
  const t = (id ?? '').trim();
  if (t === '') {
    return false;
  }
  if (t.startsWith('__')) {
    return false;
  }
  if (t.includes('#')) {
    return false;
  }
  return true;
}

@Component({
  selector: 'app-web-analytics-filters',
  imports: [
    FormsModule,
    ButtonModule,
    DatePicker,
    InputText,
    Panel,
    Select,
    TooltipModule,
  ],
  templateUrl: './web-analytics-filters.component.html',
  styleUrl: './web-analytics-filters.component.scss',
})
export class WebAnalyticsFiltersComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly ui = inject(WebAnalyticsUiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly webAnalytics = inject(WebAnalyticsService);

  readonly sidebarVisible = signal(true);
  readonly appIdOptions = signal<AppIdOption[]>([
    { value: '', label: 'All apps' },
  ]);
  readonly appIdLoading = signal(false);

  /** Empty string = all apps. */
  appId = '';
  /** True after the user changes the app dropdown (including "All apps"). */
  private appIdUserOverride = false;
  /** PrimeNG range: [start, end] */
  dateRange: Date[] | null = null;

  filterEventType = '';
  filterUsername = '';
  filterCountry = '';
  filterBrowser = '';
  filterOs = '';
  filterDevice = '';
  filterCity = '';
  filterPath = '';

  ngOnInit(): void {
    this.ui.setSidebarExpanded(this.sidebarVisible());

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((q) => {
        const parsed = dashboardFiltersFromQuery(q);
        if (!parsed) {
          return;
        }
        this.patchForm(parsed);
        this.loadAppIds(parsed.startDate, parsed.endDate);
      });
  }

  private loadAppIds(start: Date | null, end: Date | null): void {
    if (!start || !end) {
      return;
    }
    this.appIdLoading.set(true);
    this.webAnalytics
      .getAppIds(start, end)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ids) => {
          const opts: AppIdOption[] = [{ value: '', label: 'All apps' }];
          for (const id of ids) {
            if (!isCustomerAppId(id)) {
              continue;
            }
            opts.push({ value: id, label: id });
          }
          if (this.appId && !ids.includes(this.appId)) {
            opts.push({ value: this.appId, label: `${this.appId} (no data)` });
          }
          this.appIdOptions.set(opts);
          if (!this.appIdUserOverride) {
            this.appId =
              dashboardFiltersFromQuery(this.route.snapshot.queryParamMap)?.appId ??
              '';
          }
          this.appIdLoading.set(false);
        },
        error: () => {
          this.appIdLoading.set(false);
        },
      });
  }

  toggleSidebar(): void {
    this.sidebarVisible.update((v) => {
      const n = !v;
      this.ui.setSidebarExpanded(n);
      return n;
    });
  }

  onAppIdUserChange(): void {
    this.appIdUserOverride = true;
  }

  apply(): void {
    const f = this.buildFilters();
    if (!f) {
      return;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParamsFromDashboardFilters(f),
      queryParamsHandling: '',
    });
  }

  presetToday(): void {
    const d = new Date();
    this.dateRange = [d, d];
    this.apply();
  }

  presetLastDays(n: number): void {
    const end = new Date();
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - (n - 1));
    this.dateRange = [start, end];
    this.apply();
  }

  presetThisMonth(): void {
    const end = new Date();
    const start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
    this.dateRange = [start, end];
    this.apply();
  }

  private patchForm(f: WebAnalyticsDashboardFilters): void {
    this.appIdUserOverride = false;
    this.appId = f.appId;
    if (f.startDate && f.endDate) {
      this.dateRange = [f.startDate, f.endDate];
    }
    this.filterEventType = f.filterEventType ?? '';
    this.filterUsername = f.filterUsername ?? '';
    this.filterCountry = f.filterCountry ?? '';
    this.filterBrowser = f.filterBrowser ?? '';
    this.filterOs = f.filterOs ?? '';
    this.filterDevice = f.filterDevice ?? '';
    this.filterCity = f.filterCity ?? '';
    this.filterPath = f.filterPath ?? '';
  }

  private buildFilters(): WebAnalyticsDashboardFilters | null {
    const fromUrl = dashboardFiltersFromQuery(this.route.snapshot.queryParamMap);
    const [start, end] = this.dateRange ?? [];
    const appId = this.appIdUserOverride
      ? this.appId.trim()
      : (this.appId.trim() || fromUrl?.appId?.trim() || '');
    return {
      appId,
      startDate: start ?? null,
      endDate: end ?? null,
      filterEventType: this.filterEventType.trim(),
      filterUsername: this.filterUsername.trim(),
      filterCountry: this.filterCountry.trim(),
      filterBrowser: this.filterBrowser.trim(),
      filterOs: this.filterOs.trim(),
      filterDevice: this.filterDevice.trim(),
      filterCity: this.filterCity.trim(),
      filterPath: this.filterPath.trim(),
    };
  }
}
